import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getNotices, dismissNotice, type Notice } from '../api/notices';

const KEY = ['notices'];

/** Portal notices for the banner stack and the bell. A failed load shows nothing rather than breaking the layout. */
export function useNotices() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: KEY, queryFn: getNotices, staleTime: 5 * 60 * 1000 });

  const mutation = useMutation({
    mutationFn: dismissNotice,
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: KEY });
      const previous = queryClient.getQueryData<Notice[]>(KEY);
      queryClient.setQueryData<Notice[]>(KEY, (old) => old?.map((n) => (n.id === id ? { ...n, dismissed: true } : n)));
      return { previous };
    },
    onError: (_error, _id, context) => {
      // A failed dismiss rolls back silently here; the banner/bell entry just reappears.
      if (context?.previous) queryClient.setQueryData(KEY, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
    },
  });

  const notices = query.data ?? [];
  return {
    notices,
    unreadCount: notices.filter((n) => !n.dismissed).length,
    dismiss: mutation.mutate,
  };
}
